<template>
    <div v-if="product" class="bg-gray-50">
        <Banner :title="product.title[locale]" :subtitle="product.features?.[locale]?.[0]" class="mb-0" />
    </div>

    <section v-if="product" class="py-16">
        <div class="container mx-auto px-4">
            <!-- 产品概览 -->
            <div class="flex gap-12 mb-16">
                <!-- 产品图片展示区 -->
                <div class="flex-1">
                    <div class="w-full h-[400px] rounded-lg overflow-hidden mb-5 shadow-lg">
                        <img :src="currentImage" :alt="product.title[locale]" class="w-full h-full object-cover" />
                    </div>
                    <div class="grid grid-cols-4 gap-4">
                        <div v-for="(img, index) in product.gallery" 
                             :key="index"
                             class="h-20 rounded overflow-hidden cursor-pointer border-2 transition-all duration-300 hover:-translate-y-1"
                             :class="currentImage === img ? 'border-blue-600' : 'border-transparent'"
                             @click="currentImage = img">
                            <img :src="img" :alt="product.title[locale]" class="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>

                <!-- 产品信息区 -->
                <div class="flex-1">
                    <h2 class="text-3xl font-bold text-[#005005] mb-4 border-b-4 border-[#005005] pb-2">{{ product.title[locale] }}</h2>
                    <div class="inline-block bg-[#8d6e63] text-white px-4 py-1 rounded-full text-sm mb-5">
                        {{ getCategoryName(product.category) }}
                    </div>
                    <p class="text-[#212121] mb-8 leading-relaxed">{{ product.desc[locale] }}</p>

                    <!-- 产品特点 -->
                    <div class="mb-8">
                        <h3 class="text-xl font-bold text-[#005005] mb-4">{{ t('products.detail.features') }}</h3>
                        <ul class="list-disc list-inside text-[#212121] space-y-2">
                            <li v-for="(feature, index) in product.features[locale]" 
                                :key="index">
                                {{ feature }}
                            </li>
                        </ul>
                    </div>

                    <!-- 产品认证 -->
                    <div v-if="product.certifications && product.certifications.length > 0" 
                         class="flex gap-4 mb-8">
                        <div v-for="(cert, index) in product.certifications" 
                             :key="index"
                             class="flex items-center bg-slate-50 px-4 py-2.5 rounded text-sm">
                            <i :class="['fas', cert.icon, 'text-[#005005]', 'mr-2.5']"></i>
                            <span class="text-[#212121]">{{ cert.name[locale] }}</span>
                        </div>
                    </div>

                    <!-- 操作按钮 -->
                    <div>
                        <button 
                            @click="handleContactClick"
                            class="bg-[#8d6e63] text-white px-6 py-3 rounded-md hover:bg-[#5f4339] transition-colors">
                            {{ t('products.detail.contactUs') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- 产品规格 -->
            <div class="bg-white rounded-lg shadow-lg p-10 mb-16">
                <div class="flex justify-center">
                    <h2 class="text-2xl text-[#005005] mb-8 border-b-4 border-[#005005] pb-2">{{ t('products.detail.specifications') }}</h2>
                </div>
                <table class="w-full">
                    <tr v-for="(spec, index) in product.specifications" 
                        :key="index"
                        class="border-b border-slate-200 last:border-0">
                        <th class="w-1/3 py-4 px-4 bg-slate-50 text-left font-semibold text-[#005005]">
                            {{ spec.name[locale] }}
                        </th>
                        <td class="py-4 px-4 text-[#212121]">{{ spec.value[locale] }}</td>
                    </tr>
                </table>
            </div>

            <!-- 使用场景 -->
            <div class="mb-16">
                <div class="flex justify-center">
                    <h2 class="text-2xl text-[#005005] mb-8 border-b-4 border-[#005005] pb-2">{{ t('products.detail.usageScenarios') }}</h2>
                </div>
                <div class="grid grid-cols-3 gap-8">
                    <div v-for="(example, index) in product.usageExamples" 
                         :key="index"
                         class="bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:-translate-y-2.5 hover:shadow-xl">
                        <!-- <div class="h-50 overflow-hidden"> -->
                        <div class="h-[300px] overflow-hidden">
                            <img :src="example.img" 
                                 :alt="example.title[locale]"
                                 class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                        </div>
                        <div class="p-5">
                            <h3 class="text-xl text-[#005005] mb-2.5">{{ example.title[locale] }}</h3>
                            <p class="text-[#212121]">{{ example.desc[locale] }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 相关产品 -->
            <div class="bg-slate-50 py-16">
                <div class="flex justify-center">
                    <h2 class="text-2xl text-[#005005] mb-8 border-b-4 border-[#005005] pb-2">{{ t('products.detail.relatedProducts') }}</h2>
                </div>
                <div class="grid grid-cols-3 gap-8">
                    <div v-for="relatedId in product.relatedProducts" 
                         :key="relatedId"
                         class="bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:-translate-y-2.5 hover:shadow-xl">
                        <!-- <div v-if="getRelatedProduct(relatedId)" class="h-50 overflow-hidden"> -->
                        <div v-if="getRelatedProduct(relatedId)" class="h-[300px] overflow-hidden">
                            <img :src="getRelatedProduct(relatedId)?.img" 
                                 :alt="getRelatedProduct(relatedId)?.title[locale]"
                                 class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                        </div>
                        <div class="p-5">
                            <h3 class="text-xl text-[#005005] mb-2.5">{{ getRelatedProduct(relatedId)?.title[locale] }}</h3>
                            <p class="text-[#212121] mb-4">{{ getRelatedProduct(relatedId)?.desc[locale] }}</p>
                            <router-link 
                                :to="{ name: 'product-detail', params: { id: relatedId }}" 
                                class="inline-block mt-4 px-6 py-2 bg-[#8d6e63] text-white rounded-md hover:bg-[#5f4339] transition-colors">
                                {{ t('products.viewDetails') }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <div v-else class="flex items-center justify-center min-h-screen">
        <div class="text-center">
            <h2 class="text-2xl text-[#005005] mb-4">{{ t('products.detail.loading') }}</h2>
            <p class="text-[#212121]">{{ t('products.detail.pleaseWait') }}</p>
        </div>
    </div>

    <CTA
      v-if="product"
      :title="t('products.detail.cta.title')"
      :description="t('products.detail.cta.description')"
      :buttonText="t('products.detail.cta.buttonText')"
      buttonLink="/contact"
      backgroundImage="/images/56.png"
    />
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Banner from '../components/Banner.vue'
import { products, tabs, type Product } from '../data/products'
import CTA from '../components/CTA.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const product = ref<Product | null>(null)
const currentImage = ref('')

const getCategoryName = (category: string) => {
    const tab = tabs.find(t => t.key === category)
    return tab ? tab.label[locale.value] : category
}

const getRelatedProduct = (id: string) => {
    return products.find(p => p.id === id)
}

const loadProduct = (productId: string) => {
    product.value = products.find(p => p.id === productId) || null
    if (product.value) {
        currentImage.value = product.value.img
    }
    // 滚动到页面顶部
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    })
}

const handleContactClick = () => {
    router.push('/contact')
}

onMounted(() => {
    const productId = route.params.id as string
    loadProduct(productId)
})

// 监听路由参数变化
watch(() => route.params.id, (newId) => {
    if (newId) {
        loadProduct(newId as string)
    }
})
</script>

<style scoped>
/* 移除所有自定义CSS，因为已经使用Tailwind类替代 */
</style> 